import { useCallback } from 'react';
import { COLOR_CATEGORY_MAP } from '../constants/colors';
import { storage } from '../services/storage';
import {
  buildDisplayTimeFrom24h,
  computeDurationFrom24hTimes,
} from '../utils/eventUtils';
import {
  createGoogleEvent,
  updateGoogleEvent,
} from '../services/calendarService';

export default function useEventActions({
  addForm,
  editForm,
  selectedTags,
  selectedEvent,
  googleCalConnected,
  googleAccessToken,
  googleEvents,
  setGoogleEvents,
  setSyncError,
  lastScreen,
  resetAddForm,
  setCurrentScreen,
  setSelectedEvent,
}) {
  const handleSaveNewEvent = useCallback(async () => {
    const { title, date, startTime, endTime, location, color, prepTime } = addForm.fields;
    if (!title.trim()) {
      alert('Please enter an event title.');
      return;
    }

    addForm.setIsSaving(true);

    try {
      const [year, month, day] = date.split('-').map(Number);
      const displayTime = buildDisplayTimeFrom24h(startTime);
      const duration = computeDurationFrom24hTimes(startTime, endTime);
      const palette = COLOR_CATEGORY_MAP[color] || COLOR_CATEGORY_MAP['bg-blue-400'];

      let googleEventId = null;
      if (googleCalConnected && googleAccessToken) {
        try {
          const created = await createGoogleEvent(googleAccessToken, {
            title: title.trim(), date, startTime, endTime, location,
            prepTime, colorKey: color, tags: [...selectedTags],
          });
          googleEventId = created.id;
        } catch (err) {
          console.error('Failed to create Google Calendar event:', err);
          if (err.status === 401) {
            setSyncError('Google session expired. Event saved locally only.');
          }
        }
      }

      const newEvent = {
        id: googleEventId ? `google_${googleEventId}` : `local_${Date.now()}`,
        title: title.trim(),
        time: displayTime,
        duration,
        month, day, year,
        color: palette.color,
        dotColor: palette.dotColor,
        alert: true,
        tags: [...selectedTags],
        needsPrep: prepTime !== '0 minutes',
        source: googleEventId ? 'google' : 'local',
        googleEventId: googleEventId || null,
        location,
      };

      const updatedEvents = [...googleEvents, newEvent];
      setGoogleEvents(updatedEvents);
      storage.setJson('appdhd_google_events', updatedEvents);

      resetAddForm();
      setCurrentScreen(lastScreen || 'home');
    } catch (err) {
      console.error('Error saving event:', err);
      alert('Something went wrong while saving. Please try again.');
    } finally {
      addForm.setIsSaving(false);
    }
  }, [addForm, selectedTags, googleCalConnected, googleAccessToken, googleEvents, lastScreen, resetAddForm, setSyncError, setGoogleEvents, setCurrentScreen]);

  const handleSaveEditedEvent = useCallback(async () => {
    if (!selectedEvent) return;
    if (selectedEvent.source === 'apple') {
      alert('Apple Calendar events can only be edited in the Apple Calendar app.');
      return;
    }
    const { title, date, startTime, endTime, location, color, prepTime } = editForm.fields;
    if (!title.trim()) {
      alert('Please enter an event title.');
      return;
    }

    editForm.setIsSaving(true);

    try {
      const [year, month, day] = date.split('-').map(Number);
      const displayTime = buildDisplayTimeFrom24h(startTime);
      const duration = computeDurationFrom24hTimes(startTime, endTime);
      const palette = COLOR_CATEGORY_MAP[color] || COLOR_CATEGORY_MAP['bg-blue-400'];

      if (selectedEvent.googleEventId && googleCalConnected && googleAccessToken) {
        try {
          await updateGoogleEvent(googleAccessToken, selectedEvent.googleEventId, {
            title: title.trim(), date, startTime, endTime, location,
            prepTime, colorKey: color, tags: [...selectedTags],
          });
        } catch (err) {
          console.error('Failed to update Google Calendar event:', err);
          if (err.status === 401) {
            setSyncError('Google session expired. Changes saved locally only.');
          }
        }
      }

      const updatedEvent = {
        ...selectedEvent,
        title: title.trim(),
        time: displayTime,
        duration,
        month, day, year,
        color: palette.color,
        dotColor: palette.dotColor,
        tags: [...selectedTags],
        needsPrep: prepTime !== '0 minutes',
        location,
      };

      const updatedEvents = googleEvents.map(e =>
        e.id === selectedEvent.id ? updatedEvent : e
      );
      setGoogleEvents(updatedEvents);
      storage.setJson('appdhd_google_events', updatedEvents);

      setSelectedEvent(updatedEvent);
      setCurrentScreen(lastScreen || 'home');
    } catch (err) {
      console.error('Error saving edited event:', err);
      alert('Something went wrong while saving. Please try again.');
    } finally {
      editForm.setIsSaving(false);
    }
  }, [editForm, selectedTags, selectedEvent, googleCalConnected, googleAccessToken, googleEvents, lastScreen, setSyncError, setGoogleEvents, setCurrentScreen, setSelectedEvent]);

  return { handleSaveNewEvent, handleSaveEditedEvent };
}
